/**
 * 主框架布局 —— 平台运营端与租户端共用
 *
 * 差异全部由「主体类型 + 权限码」推导：
 *   - 平台运营人员看到 PLATFORM_MENUS，租户用户看到 TENANT_MENUS；
 *   - 两侧菜单都按权限码过滤，没有权限的菜单项直接不渲染；
 *   - 顶栏明确标识当前身份（平台运营 / 租户名），避免运营人员误操作租户数据。
 */

import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Divider,
  Dropdown,
  Empty,
  Layout,
  List,
  Menu,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { MessageType, MESSAGE_TYPE_LABELS, PrincipalType, type Message } from '@ilc/shared';
import { useAuthStore } from '@/stores/auth';
import { findMenuTrail, flattenMenus, PLATFORM_MENUS, TENANT_MENUS, type MenuNode } from '@/router/menu';
import { messageApi } from '@/api/endpoints';
import { useResource } from '@/hooks/useResource';
import { mockMessageList, mockUnreadCount } from '@/mocks';

const { Header, Sider, Content } = Layout;

/** 按权限与主体过滤菜单：无权限的菜单项不渲染 */
function filterMenus(nodes: MenuNode[], can: (code: string) => boolean, isPlatform: boolean): MenuNode[] {
  const out: MenuNode[] = [];
  for (const node of nodes) {
    if (node.platformOnly && !isPlatform) continue;
    if (node.tenantOnly && isPlatform) continue;

    if (node.children?.length) {
      const children = filterMenus(node.children, can, isPlatform);
      if (children.length) out.push({ ...node, children });
      continue;
    }
    if (node.permission && !can(node.permission)) continue;
    out.push(node);
  }
  return out;
}

function toMenuItems(nodes: MenuNode[]): Array<{
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  children?: ReturnType<typeof toMenuItems>;
}> {
  return nodes.map((node) => ({
    key: node.key,
    icon: node.icon,
    label: node.hint ? (
      <Tooltip title={node.hint} placement="right">
        <span>{node.label}</span>
      </Tooltip>
    ) : (
      node.label
    ),
    ...(node.children?.length ? { children: toMenuItems(node.children) } : {}),
  }));
}

/** 顶部消息提醒 */
function MessageBell(): React.ReactElement {
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const isPlatform = useAuthStore((s) => s.auth?.principalType === PrincipalType.PLATFORM);
  const [open, setOpen] = useState(false);

  // 站内消息是租户域数据（/api/messages/*），平台运营账号没有租户上下文，调用会被后端拒绝（403）。
  // 平台侧的通知走平台审计日志，因此这里对平台主体直接不下发请求、也不渲染铃铛。
  const unread = useResource(() => messageApi.unreadCount(), {
    fallback: () => mockUnreadCount(),
    enabled: !isPlatform,
  });
  const list = useResource(() => messageApi.list({ page: 1, pageSize: 8, isRead: false }), {
    fallback: () => mockMessageList({ page: 1, pageSize: 8, isRead: false }),
    enabled: open && !isPlatform,
  });

  if (isPlatform || !can('message:read')) return <></>;

  const messages: Message[] = list.data?.list ?? [];

  const content = (
    <div style={{ width: 360 }}>
      {list.loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Typography.Text type="secondary">加载中…</Typography.Text>
        </div>
      ) : messages.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无未读消息" />
      ) : (
        <List
          size="small"
          dataSource={messages}
          renderItem={(item) => (
            <List.Item
              style={{ cursor: item.link ? 'pointer' : 'default' }}
              onClick={() => {
                setOpen(false);
                if (item.link) navigate(item.link);
              }}
            >
              <List.Item.Meta
                title={
                  <Space size={4}>
                    <Tag
                      color={item.type === MessageType.RISK_ALERT ? 'red' : item.type === MessageType.PROVIDER_ERROR ? 'orange' : 'blue'}
                      style={{ marginInlineEnd: 0 }}
                    >
                      {MESSAGE_TYPE_LABELS[item.type]}
                    </Tag>
                    <span style={{ fontSize: 13 }}>{item.title}</span>
                  </Space>
                }
                description={
                  <Typography.Paragraph
                    type="secondary"
                    ellipsis={{ rows: 2 }}
                    style={{ marginBottom: 0, fontSize: 12 }}
                  >
                    {item.content}
                  </Typography.Paragraph>
                }
              />
            </List.Item>
          )}
        />
      )}
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ textAlign: 'center' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          消息与待办提醒规则可在「租户配置 → 消息规则」调整
        </Typography.Text>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', padding: 12 }}>
          {content}
        </div>
      )}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button type="text" icon={<Badge count={unread.data?.total ?? 0} size="small"><BellOutlined style={{ fontSize: 16 }} /></Badge>} />
    </Dropdown>
  );
}

export function MainLayout(): React.ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const auth = useAuthStore((s) => s.auth);
  const can = useAuthStore((s) => s.can);
  const logout = useAuthStore((s) => s.logout);

  const isPlatform = auth?.principalType === PrincipalType.PLATFORM;

  const menus = useMemo(() => {
    const source = isPlatform ? PLATFORM_MENUS : TENANT_MENUS;
    return filterMenus(source, can, isPlatform);
  }, [isPlatform, can]);

  const menuItems = useMemo(() => toMenuItems(menus), [menus]);

  const selectedKeys = useMemo(() => {
    const leaves = flattenMenus(menus);
    // 最长前缀匹配：详情页 /business/invoices/inv-003 也高亮「我的发票」
    const matched = leaves
      .filter((n) => n.path && location.pathname.startsWith(n.path))
      .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0));
    if (matched[0]?.path) return [matched[0].path];
    return [location.pathname];
  }, [menus, location.pathname]);

  const trail = useMemo(() => {
    const source = isPlatform ? PLATFORM_MENUS : TENANT_MENUS;
    return findMenuTrail(source, location.pathname);
  }, [isPlatform, location.pathname]);

  const roleNames = auth?.roles.map((r) => r.name).join('、') || (isPlatform ? '平台运营' : '未分配角色');

  const breadcrumbItems = [
    { title: isPlatform ? '平台运营端' : '租户管理端' },
    ...trail.map((node) => ({ title: node.label })),
    // 详情页在菜单里匹配不到，补一个可读的尾节点
    ...(trail.length === 0 && location.pathname !== '/dashboard' && location.pathname !== '/profile'
      ? [{ title: '详情' }]
      : []),
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} trigger={null} width={216} theme="dark" breakpoint="lg">
        <div className="ilc-logo">
          <SafetyCertificateOutlined style={{ fontSize: 20, color: '#69b1ff' }} />
          {!collapsed ? <span>票链云</span> : null}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={menus.filter((m) => m.children?.length).map((m) => m.key)}
          items={menuItems}
          onClick={({ key }) => {
            if (key.startsWith('/')) navigate(key);
          }}
        />
      </Sider>

      <Layout>
        <Header className="ilc-header">
          <Space size={12}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((v) => !v)}
            />
            <Breadcrumb items={breadcrumbItems} />
          </Space>

          <Space size={12}>
            {isPlatform ? (
              <Tag icon={<BankOutlined />} color="geekblue">
                平台运营端
              </Tag>
            ) : (
              <Tag icon={<SafetyCertificateOutlined />} color="blue">
                {auth?.tenantName ?? '租户'}
                {auth?.tenantId ? '' : ''}
              </Tag>
            )}
            <MessageBell />
            <Tooltip title="帮助：各角色的操作指引见工作台首页说明">
              <Button type="text" icon={<QuestionCircleOutlined style={{ fontSize: 16 }} />} href="/dashboard" />
            </Tooltip>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'profile',
                    icon: <UserOutlined />,
                    label: '个人信息',
                    onClick: () => navigate('/profile'),
                  },
                  { type: 'divider' as const },
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: '退出登录',
                    onClick: () => void logout(),
                  },
                ],
              }}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" style={{ background: '#1677ff' }}>
                  {(auth?.name ?? 'U').slice(0, 1)}
                </Avatar>
                <span style={{ fontSize: 13 }}>{auth?.name}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {roleNames}
                </Typography.Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: 16, overflow: 'auto' }}>
          <Outlet />
          <div style={{ textAlign: 'center', padding: '24px 0 8px', color: 'rgba(0,0,0,0.35)', fontSize: 12 }}>
            票链云 · 发票核验·财务记账数字化平台 · 本平台仅同步业务数据，凭证由 T+ 端人工选择科目生成
            {' · '}
            <Link to="/profile">账号信息</Link>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default MainLayout;
